import { Menu, Pagination, Table, Text, Tooltip } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import * as React from "react";
import type { ReturnOrderLine } from "../types";
import { normalizeSerial } from "../utils/serial";
import { getZoomSafePortalTarget } from "../utils/zoomSafePortal";

const PAGE_SIZE = 10;

const HEAD_CELL: React.CSSProperties = {
  height: 36,
  padding: "0 10px",
  background: "#F6F7F4",
  color: "#5C6570",
  fontSize: 12,
  fontWeight: 600,
  whiteSpace: "nowrap",
  borderBottom: "1px solid #ECEEE9",
};

const BODY_CELL: React.CSSProperties = {
  padding: "6px 10px",
  fontSize: 12.5,
  color: "#1B1F24",
  borderBottom: "1px solid #F0F1EE",
};

interface ItemSearchMenuProps {
  keyword: string;
  onKeywordChange: (keyword: string) => void;
  /** Dòng hàng khách trả của đơn: tìm ngay trên các dòng này (mỗi dòng một serial) */
  lines: ReturnOrderLine[];
  disabled: boolean;
  /** Serial đã có trên phiếu, để đánh dấu "Đã chọn" */
  scannedSerials: Set<string>;
  onPick: (serial: string) => void;
  /** Tăng mỗi lần bấm nút trên header: đóng bảng kết quả */
  closeSignal: number;
  /** Tăng mỗi lần chọn mã ở bảng đơn hàng: mở lại bảng, kể cả chọn lại đúng mã đang tìm */
  openSignal: number;
}

/**
 * Tìm serial khách trả theo mã / tên sản phẩm / serial, bấm "Chọn" để quét serial đó vào phiếu.
 * Lọc trên dòng đơn đã tải thay vì gọi search-list-serial-item-transaction: API đó với đổi trả
 * không lọc theo đơn nên trả cả serial của đơn khác.
 */
const ItemSearchMenu = ({
  keyword,
  onKeywordChange,
  lines,
  disabled,
  scannedSerials,
  onPick,
  closeSignal,
  openSignal,
}: ItemSearchMenuProps) => {
  const [debounced] = useDebouncedValue(keyword, 200);
  const [page, setPage] = React.useState(1);
  const [focused, setFocused] = React.useState(false);
  // Bấm nút trên header thì đóng bảng; từ khoá đổi, chọn mã ở bảng đơn hàng hoặc bấm lại vào ô tìm thì mở lại
  const [dismissed, setDismissed] = React.useState(false);
  React.useEffect(() => {
    if (closeSignal) setDismissed(true);
  }, [closeSignal]);
  React.useEffect(() => setDismissed(false), [keyword, openSignal]);

  React.useEffect(() => setPage(1), [debounced]);

  const matches = React.useMemo(() => {
    const key = normalizeSerial(debounced);
    if (!key) return [];
    return lines.filter(
      (line) =>
        line.serial &&
        [line.itemCode, line.itemName, line.serial].some((value) => normalizeSerial(value).includes(key)),
    );
  }, [debounced, lines]);

  const totalPages = Math.max(1, Math.ceil(matches.length / PAGE_SIZE));
  const pageItems = matches.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const active = focused && !disabled;

  return (
    <Menu
      opened={!disabled && matches.length > 0 && !dismissed}
      trapFocus={false}
      shadow="md"
      width={720}
      position="bottom-start"
      offset={8}
      // Body đang zoom 93%: render vào khung bù zoom để bảng kết quả nằm ngay dưới ô tìm kiếm
      portalProps={{ target: getZoomSafePortalTarget() }}
      styles={{ dropdown: { padding: 0, border: "1px solid #DDE0DA", borderRadius: 12, overflow: "hidden" } }}
    >
      <Menu.Target>
        <label
          style={{
            width: 210,
            height: 38,
            boxSizing: "border-box",
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "0 10px",
            border: `1px solid ${active ? "#1F5FAE" : "#D5D9D2"}`,
            borderRadius: 8,
            boxShadow: active ? "0 0 0 3px #E3ECF7" : "none",
            background: disabled ? "#F6F7F4" : "#FFFFFF",
            cursor: disabled ? "not-allowed" : "text",
            flexShrink: 0,
          }}
        >
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke={disabled ? "#A3AAB2" : "#5C6570"}
            strokeWidth="2"
            strokeLinecap="round"
            aria-hidden="true"
            style={{ flexShrink: 0 }}
          >
            <circle cx="11" cy="11" r="7" />
            <path d="M20 20l-4-4" />
          </svg>
          <input
            type="text"
            aria-label="Tìm kiếm mã sản phẩm"
            placeholder="Tìm kiếm mã sản phẩm"
            autoComplete="off"
            value={keyword}
            disabled={disabled}
            onFocus={() => {
              setFocused(true);
              setDismissed(false);
            }}
            onClick={() => setDismissed(false)}
            onBlur={() => setFocused(false)}
            onKeyDown={(e) => {
              if (e.key === "Escape") setDismissed(true);
            }}
            onChange={(e) => onKeywordChange(e.currentTarget.value ?? "")}
            style={{
              flexGrow: 1,
              minWidth: 0,
              border: 0,
              outline: "none",
              padding: 0,
              font: "inherit",
              fontSize: 13,
              color: disabled ? "#8A929B" : "#1B1F24",
              background: "transparent",
              cursor: "inherit",
            }}
          />
        </label>
      </Menu.Target>
      <Menu.Dropdown>
        <Table.ScrollContainer minWidth={520} mah={420} type="native">
          <Table w="100%" highlightOnHover stickyHeader>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={HEAD_CELL} />
                <Table.Th style={HEAD_CELL}>Mã sản phẩm</Table.Th>
                <Table.Th style={HEAD_CELL}>Tên sản phẩm</Table.Th>
                <Table.Th style={HEAD_CELL}>Số serial</Table.Th>
                <Table.Th style={HEAD_CELL}>Đơn vị tính</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {pageItems.map((line) => {
                const serial = String(line.serial ?? "");
                const picked = scannedSerials.has(normalizeSerial(serial));
                return (
                  <Table.Tr key={`${line.sourceLineId ?? ""}-${serial}`} bg={picked ? "#F2F7FD" : undefined}>
                    <Table.Td style={BODY_CELL}>
                      <button
                        type="button"
                        disabled={picked}
                        onClick={() => onPick(serial)}
                        style={{
                          height: 28,
                          padding: "0 10px",
                          border: `1px solid ${picked ? "#C9D6E8" : "#1F5FAE"}`,
                          borderRadius: 6,
                          background: picked ? "#E6EEF8" : "#1F5FAE",
                          color: picked ? "#1F5FAE" : "#FFFFFF",
                          font: "inherit",
                          fontSize: 12,
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                          cursor: picked ? "default" : "pointer",
                        }}
                      >
                        {picked ? "Đã chọn" : "Chọn"}
                      </button>
                    </Table.Td>
                    <Table.Td style={BODY_CELL}>{line.itemCode}</Table.Td>
                    <Table.Td style={BODY_CELL}>
                      <Tooltip label={line.itemName}>
                        <Text w={260} lineClamp={2} style={{ fontSize: 12.5 }}>
                          {line.itemName}
                        </Text>
                      </Tooltip>
                    </Table.Td>
                    <Table.Td style={{ ...BODY_CELL, fontWeight: 500 }}>{serial}</Table.Td>
                    <Table.Td style={BODY_CELL}>{line.primaryUom}</Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
        {totalPages > 1 && (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              padding: "8px 14px",
              borderTop: "1px solid #ECEEE9",
              background: "#FAFBF8",
            }}
          >
            <Pagination total={totalPages} value={page} withEdges onChange={setPage} size="sm" color="#1F5FAE" />
          </div>
        )}
      </Menu.Dropdown>
    </Menu>
  );
};

export default ItemSearchMenu;
